import { LockClosedIcon, LockOpen1Icon } from '@radix-ui/react-icons'
import * as Switch from '@radix-ui/react-switch'
import { clsx } from 'clsx'
import * as React from 'react'
import { useUserStore } from '~/app/_store/user-store'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { AnnouncementHighlight } from '~/sotano-kit/primitives/announcement-highlight'
import { commandKeyPressed } from '~/lib/command-key-pressed'
import { useIsGuestInHisBranch } from '~/hooks-data/use-user-role'

export const LockSchemaSwitch = () => {
  const teamReplicache = useTeamReplicache()?.data
  const repoReplicache = useRepoReplicache()?.data
  const userId = useUserStore((state) => state.optimisticUser?.id)
  const isGuestInHisBranch = useIsGuestInHisBranch()

  const trueRole = React.useMemo(() => {
    const teamMember = teamReplicache?.team?.members.find(
      (member) => member.userId === userId
    )
    return teamMember?.role ?? ''
  }, [teamReplicache, userId])

  const editMode = useUserPreferences((s) => s.editMode)
  const toggleLockSchema = React.useCallback(() => {
    if (!userId) return
    repoReplicache?.replicache.mutate.setEditMode({
      editMode: !editMode,
      userId,
    })
  }, [editMode, repoReplicache, userId])

  const isMemberOrHigher = ['owner', 'member'].includes(trueRole)
  const isEditor = trueRole === 'editor'
  const canManageSchema = isMemberOrHigher || isGuestInHisBranch

  const isMacOs = React.useMemo(
    () =>
      typeof navigator === 'undefined' ||
      navigator.userAgent.indexOf('Mac OS X'),
    []
  )

  React.useLayoutEffect(() => {
    if (!canManageSchema) return

    const onNavigateThroughRepo = (e: KeyboardEvent) => {
      if (e.key === 'e' && commandKeyPressed(e)) {
        e.preventDefault()
        toggleLockSchema()
      }
    }

    document.addEventListener('keydown', onNavigateThroughRepo)
    return () => {
      document.removeEventListener('keydown', onNavigateThroughRepo)
    }
  }, [canManageSchema, toggleLockSchema])

  if (!isMemberOrHigher && !isEditor && !isGuestInHisBranch) return null

  return (
    <AnnouncementHighlight align="end" announcementId="edit-mode">
      {({ isHighlighted }) => {
        return (
          <SimpleTooltip
            disabled={isHighlighted}
            collisionPadding={16}
            content={
              canManageSchema
                ? (editMode ? 'Unlock' : 'Lock') +
                  ` schema (${isMacOs ? '⌘' : 'ctrl'} + E)`
                : `Forced edit mode due to your role`
            }
          >
            <Switch.Root
              checked={canManageSchema ? editMode : true}
              onCheckedChange={
                canManageSchema ? toggleLockSchema : () => undefined
              }
              className="bg-dark-elevated relative cursor-default p-px rounded-full w-[38px] h-navbar-element group/switch outline-1 outline-offset-2 outline-dark-control-base focus-visible:outline"
            >
              <Switch.Thumb
                className={clsx(
                  'flex items-center justify-center size-[18px] bg-dark-bg group-hover/switch:bg-dark-shade-hover text-dark-bright',
                  'rounded-full transition-[transform_colors] duration-100 will-change-transform translate-x-px',
                  'radix-state-checked:translate-x-[17px] radix-state-checked:bg-dark-control-base group-hover/switch:radix-state-checked:bg-dark-control-highlight radix-state-checked:text-dark-bg'
                )}
              >
                {editMode ? (
                  <LockClosedIcon
                    height={12}
                    width={12}
                    className="text-[#f3f3f3]"
                  />
                ) : (
                  <LockOpen1Icon height={12} width={12} />
                )}
              </Switch.Thumb>
            </Switch.Root>
          </SimpleTooltip>
        )
      }}
    </AnnouncementHighlight>
  )
}
